const EARTH_RADIUS_KM = 6371.0088;

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function geometryFromGeoJson(geoJson) {
  if (!geoJson) return null;
  if (typeof geoJson === 'string') {
    try {
      return geometryFromGeoJson(JSON.parse(geoJson));
    } catch (_error) {
      return null;
    }
  }
  return geoJson.geometry || geoJson;
}

export function geometryBoundaryPolygons(geoJson) {
  const geometry = geometryFromGeoJson(geoJson);
  const coordinates = geometry?.coordinates;
  const outerRings = geometry?.type === 'MultiPolygon'
    ? coordinates?.map(polygon => polygon?.[0])
    : [coordinates?.[0]];

  return (outerRings || [])
    .map(ring => (ring || [])
      .map(([lng, lat]) => [asNumber(lat), asNumber(lng)])
      .filter(([lat, lng]) => lat !== null && lng !== null))
    .filter(ring => ring.length >= 3);
}

export function haversineDistanceKm(a, b) {
  const lat1 = asNumber(a?.lat);
  const lng1 = asNumber(a?.lng);
  const lat2 = asNumber(b?.lat);
  const lng2 = asNumber(b?.lng);
  if ([lat1, lng1, lat2, lng2].some(value => value === null)) return null;
  const radians = value => value * Math.PI / 180;
  const dLat = radians(lat2 - lat1);
  const dLng = radians(lng2 - lng1);
  const value = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(value));
}

export function pointInsideGeometry(point, geoJson) {
  const geometry = geometryFromGeoJson(geoJson);
  const rings = geometry?.type === 'MultiPolygon'
    ? geometry.coordinates?.map(polygon => polygon?.[0])
    : [geometry?.coordinates?.[0]];
  const x = asNumber(point?.lng);
  const y = asNumber(point?.lat);
  if (x === null || y === null) return false;

  return (rings || []).some(ring => {
    if (!Array.isArray(ring) || ring.length < 3) return false;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = asNumber(ring[i]?.[0]);
      const yi = asNumber(ring[i]?.[1]);
      const xj = asNumber(ring[j]?.[0]);
      const yj = asNumber(ring[j]?.[1]);
      if ([xi, yi, xj, yj].some(value => value === null)) continue;
      const intersects = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / ((yj - yi) || Number.EPSILON) + xi);
      if (intersects) inside = !inside;
    }
    return inside;
  });
}

export function propertyReferencePoint(property) {
  const polygons = geometryBoundaryPolygons(property?.geometria);
  if (polygons.length) {
    const points = polygons.flatMap(ring => {
      const [firstLat, firstLng] = ring[0] || [];
      const [lastLat, lastLng] = ring[ring.length - 1] || [];
      const isClosed = firstLat === lastLat && firstLng === lastLng;
      return isClosed ? ring.slice(0, -1) : ring;
    });
    if (points.length) {
      const total = points.reduce(
        (sum, [lat, lng]) => ({ lat: sum.lat + lat, lng: sum.lng + lng }),
        { lat: 0, lng: 0 }
      );
      return { lat: total.lat / points.length, lng: total.lng / points.length };
    }
  }
  const lat = asNumber(property?.coordenada_y ?? property?.latitude);
  const lng = asNumber(property?.coordenada_x ?? property?.longitude);
  return lat === null || lng === null ? null : { lat, lng };
}

export function validateStationLocation(property, station, maximumDistanceKm = 25) {
  const point = {
    lat: asNumber(station?.latitude ?? station?.lat),
    lng: asNumber(station?.longitude ?? station?.lng ?? station?.lon),
  };
  if (point.lat === null || point.lng === null) {
    return { valid: false, code: 'station_without_location', message: 'O sensor precisa ter uma localização válida.' };
  }
  if (pointInsideGeometry(point, property?.geometria)) {
    return { valid: true, code: 'inside_property', distanceKm: 0 };
  }
  const reference = propertyReferencePoint(property);
  if (!reference) {
    return {
      valid: false,
      code: 'property_without_location',
      message: property?.geometria
        ? 'Não foi possível comparar as coordenadas da estação com a propriedade. Revise o georreferenciamento.'
        : 'Georreferencie a propriedade na Central de Operações antes de vincular a estação.',
    };
  }
  const distanceKm = haversineDistanceKm(point, reference);
  const valid = distanceKm !== null && distanceKm <= maximumDistanceKm;
  return {
    valid,
    code: valid ? 'near_property' : 'too_far',
    distanceKm,
    message: valid
      ? null
      : distanceKm === null
        ? 'Não foi possível comparar as coordenadas da estação com a propriedade. Revise o georreferenciamento.'
        : `A estação está a aproximadamente ${Math.round(distanceKm)} km da propriedade selecionada. Revise o vínculo e a localização.`,
  };
}

export function findCompatibleProperty(properties = [], station, maximumDistanceKm = 25) {
  const comparisons = properties.map(property => ({
    property,
    validation: validateStationLocation(property, station, maximumDistanceKm),
  }));
  const valid = comparisons
    .filter(item => item.validation.valid)
    .sort((left, right) => (left.validation.distanceKm ?? 0) - (right.validation.distanceKm ?? 0));
  if (valid.length) return valid[0];

  const nearest = comparisons
    .filter(item => Number.isFinite(item.validation.distanceKm))
    .sort((left, right) => left.validation.distanceKm - right.validation.distanceKm)[0];

  return nearest || {
    property: null,
    validation: {
      valid: false,
      code: 'no_mapped_property',
      message: 'Nenhuma propriedade georreferenciada permite validar esta estação.',
    },
  };
}
